import type { TimeOfDayValue, TimeRange } from '@/wabunka-src/common/components/stateful/TimeOfDayPicker/types';

export interface TimeOfDayOption {
  value: TimeOfDayValue;
  label: string;
  detail: string;
}

export const timeOfDayOptions: TimeOfDayOption[] = [
  { value: 'morning', label: 'Morning', detail: 'Starts before 11am' },
  { value: 'afternoon', label: 'Afternoon', detail: 'Starts after 11am' },
  { value: 'evening', label: 'Evening', detail: 'Starts after 5pm' },
];

// 各時間帯の範囲定義
const timeRanges: Record<TimeOfDayValue, TimeRange> = {
  morning: { from: '04:00', to: '10:59' },
  afternoon: { from: '11:00', to: '16:59' },
  evening: { from: '17:00', to: '03:59' },
};

// 選択された時間帯をマージして単一の範囲を返す
export const calculateMergedTimeRange = (selected: Set<TimeOfDayValue>): TimeRange | null => {
  if (selected.size === 0) {
    return null;
  }

  const hasMorning = selected.has('morning');
  const hasAfternoon = selected.has('afternoon');
  const hasEvening = selected.has('evening');

  // 3つ全て選択 → 全時間帯
  if (hasMorning && hasAfternoon && hasEvening) {
    return { from: '04:00', to: '03:59' };
  }

  // Morning + Afternoon
  if (hasMorning && hasAfternoon && !hasEvening) {
    return { from: timeRanges.morning.from, to: timeRanges.afternoon.to };
  }

  // Afternoon + Evening
  if (!hasMorning && hasAfternoon && hasEvening) {
    return { from: timeRanges.afternoon.from, to: timeRanges.evening.to };
  }

  // Morning + Evening (隣接しないが、仕様上は隣接扱い)
  if (hasMorning && !hasAfternoon && hasEvening) {
    return { from: timeRanges.evening.from, to: timeRanges.morning.to };
  }

  // 単一選択
  if (hasMorning) {
    return timeRanges.morning;
  }
  if (hasAfternoon) {
    return timeRanges.afternoon;
  }
  if (hasEvening) {
    return timeRanges.evening;
  }

  return null;
};

interface UseTimeOfDayOptions {
  defaultSelect?: TimeOfDayValue[];
  onSelectionChange?: (timeOfDay: Set<TimeOfDayValue>) => void;
  onTimeRangeChange?: (timeRange: TimeRange | null) => void;
}

export const useTimeOfDay = (options: UseTimeOfDayOptions = {}) => {
  const selectedTimeOfDay = ref<Set<TimeOfDayValue>>(new Set(options.defaultSelect ?? []));

  const toggleTimeOfDay = (value: TimeOfDayValue) => {
    if (selectedTimeOfDay.value.has(value)) {
      selectedTimeOfDay.value.delete(value);
    } else {
      selectedTimeOfDay.value.add(value);
    }
    // 新しいSetを作成してリアクティビティを発火
    selectedTimeOfDay.value = new Set(selectedTimeOfDay.value);
    options.onSelectionChange?.(selectedTimeOfDay.value);
    options.onTimeRangeChange?.(calculateMergedTimeRange(selectedTimeOfDay.value));
  };

  return {
    selectedTimeOfDay,
    toggleTimeOfDay,
    timeOfDayOptions,
  };
};
